// CSV Export Utility
// Handles CSV generation using Web Workers for large datasets

export const exportTableToCSV = (data, columns, filename) => {
    // Create an object to handle both progress updates and completion
    const csvExportHandler = {
        // Store the progress callback function
        progressCallback: null,

        // Method to register a progress callback
        onProgress: function (callback) {
            this.progressCallback = callback;
            return this; // Allow chaining
        },

        // Start the CSV generation
        start: function () {
            // Store reference to the handler for use in callbacks
            const self = this;

            return new Promise((resolve, reject) => {
                if (!data || !data.length) {
                    reject(new Error('No data to export'));
                    return;
                }

                // Prepare a simplified version of columns for the worker
                const serializableColumns = columns.map(column => ({
                    header: typeof column.header === 'string' ? column.header : column.accessorKey || column.id,
                    accessorKey: column.accessorKey,
                    // Only preserve accessorFn if it exists
                    ...(column.accessorFn && { accessorFn: column.accessorFn.toString() })
                }));

                // Create a new worker
                const worker = new Worker(new URL('../workers/csvWorker.js', import.meta.url), { type: 'module' });
                let csvContent = '';

                // Prepare for batch processing
                const BATCH_SIZE = 1000; // Process 1000 rows at a time
                const totalBatches = Math.ceil(data.length / BATCH_SIZE);
                let processedBatches = 0;

                // Handle messages from the worker
                worker.onmessage = (e) => {
                    const { status, csvContent: batchContent, error } = e.data;

                    if (status === 'error') {
                        worker.terminate();
                        reject(new Error(error || 'Error generating CSV'));
                        return;
                    }

                    // Append this batch's content to the full CSV
                    csvContent += batchContent;
                    processedBatches++;

                    // Calculate progress percentage
                    const progress = Math.floor((processedBatches / totalBatches) * 100);

                    // Call the progress callback if it exists
                    if (self.progressCallback) {
                        self.progressCallback(progress);
                    }

                    // If all batches are processed, finalize the CSV
                    if (processedBatches >= totalBatches) {
                        // Create a Blob with the CSV data
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

                        // Clean up
                        worker.terminate();

                        // Create a download function
                        const download = () => {
                            // Create download URL
                            const url = URL.createObjectURL(blob);

                            // Create a link element to trigger download
                            const link = document.createElement('a');
                            link.setAttribute('href', url);
                            link.setAttribute('download', filename || 'export.csv');
                            document.body.appendChild(link);

                            // Trigger download
                            link.click();

                            // Clean up
                            setTimeout(() => {
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                            }, 100);
                        };

                        // Resolve the promise with the result
                        resolve({
                            status: 'complete',
                            rowCount: data.length,
                            download
                        });
                    }
                };

                // Begin processing in batches
                for (let i = 0; i < totalBatches; i++) {
                    const startIndex = i * BATCH_SIZE;
                    const endIndex = Math.min(startIndex + BATCH_SIZE, data.length);
                    const batchData = data.slice(startIndex, endIndex);

                    // Send batch to worker
                    worker.postMessage({
                        action: 'generateCSV',
                        data: batchData,
                        columns: serializableColumns,
                        batchIndex: i,
                        totalBatches
                    });
                }
            });
        }
    };

    return csvExportHandler;
}; 